import { FEATURE_CONTROLS, isFeatureControlValue } from './featureControls.js';
export { FEATURE_CONTROLS } from './featureControls.js';

export const ADMIN_SECTIONS = [
  { key: 'details', label: 'Shop details', icon: 'fa-solid fa-store' },
  { key: 'mso', label: 'MSO', icon: 'fa-solid fa-building' },
  { key: 'texting', label: 'Texting', icon: 'fa-solid fa-comment-sms' },
  { key: 'features', label: 'Features & integrations', icon: 'fa-solid fa-sliders' },
];

export const DETAILS_FIELDS = [
  { key: 'shop_name', label: 'Shop name', required: true, max: 255 },
  { key: 'public_name', label: 'Public name', max: 255 },
  {
    key: 'subdomain_shop_name',
    label: 'Subdomain',
    required: true,
    max: 100,
    pattern: /^[a-z0-9]+$/,
    patternMessage: 'Use lowercase letters and numbers.',
    sensitive: true,
  },
  { key: 'shop_phone_number', label: 'Phone', type: 'tel', max: 20 },
  {
    key: 'shop_email',
    label: 'Email addresses',
    type: 'emails',
    max: 1000,
    help: 'Separate multiple addresses with commas.',
  },
  { key: 'time_zone', label: 'Timezone', type: 'select', required: true },
  { key: 'shop_website', label: 'Website', type: 'url', max: 255 },
  { key: 'shop_signature', label: 'Signature', type: 'textarea', max: 500 },
];

export const ADDRESS_FIELDS = [
  { key: 'street_name', label: 'Street', max: 255 },
  { key: 'city', label: 'City', max: 100 },
  { key: 'state', label: 'State / province', max: 50 },
  { key: 'zipcode', label: 'Postal code', max: 20 },
  { key: 'country', label: 'Country', type: 'select' },
];

export const TEXTING_FIELDS = [
  { key: 'shop_text_phone_number', label: 'Text number', type: 'tel', max: 20, sensitive: true },
  {
    key: 'twilio_sid',
    label: 'Twilio account SID',
    max: 34,
    pattern: /^AC[a-fA-F0-9]{32}$/,
    patternMessage: 'Enter a valid Twilio account SID.',
    sensitive: true,
  },
];

export const USER_FIELDS = [
  { key: 'first_name', label: 'First name', required: true, max: 100 },
  { key: 'last_name', label: 'Last name', required: true, max: 100 },
  { key: 'username', label: 'Username', required: true, max: 15 },
  { key: 'email', label: 'Email', type: 'email', max: 255 },
  { key: 'cell_number', label: 'Mobile number', type: 'tel', max: 20 },
  { key: 'role_id', label: 'Role', type: 'select', required: true },
];

export const USER_STATUSES = [
  { label: 'Awaiting verification', value: 0 },
  { label: 'Active', value: 1 },
  { label: 'Suspended', value: 2 },
  { label: 'Locked out', value: 3 },
  { label: 'MSO read-only', value: 5 },
];

export const OPERATIONS = [
  'activation',
  'details',
  'address',
  'features',
  'texting',
  'user-create',
  'user-update',
  'user-delete',
  'user-unlock',
  'user-password',
  'mso-password',
  'user-ghost',
  'mso',
  'mso-master',
  'mso-reset',
  'mso-assign',
  'number-add',
  'number-remove',
  'twilio',
  'session-refresh',
  'labor-guide',
  'parts-provider',
  'kiosk-access',
];

export const ownershipLabel = (value) =>
  ({ bayhive: 'BayHive-owned', autoflow: 'Autoflow', unclassified: 'Ownership unverified' })[
    value
  ] || 'Ownership unverified';

export const normalizeAccount = (raw = {}) => {
  const target = raw.target || {};
  const owned = target.ownership === 'bayhive';
  const capabilities = Array.isArray(raw.capabilities) ? raw.capabilities : [];
  const allowed = capabilities.filter(
    (key) => OPERATIONS.includes(key) && (owned || key === 'activation'),
  );
  const base = {
    target: {
      id: target.id,
      name: target.name || 'Shop',
      ownership: target.ownership || 'unclassified',
      enabled: target.enabled === true,
    },
    capabilities: allowed,
  };
  if (!owned) return base;
  return {
    ...raw,
    ...base,
    features: Object.fromEntries(
      FEATURE_CONTROLS.filter((field) => Object.hasOwn(raw.features || {}, field.key)).map(
        (field) => [field.key, raw.features[field.key]],
      ),
    ),
  };
};

export const validateFields = (fields, values) => {
  const errors = {};
  for (const field of fields) {
    if (field.disabled) continue;
    const value = values[field.key];
    const blank = value == null || (typeof value === 'string' && !value.trim());
    let error = '';
    if (field.required && blank) error = `${field.label} is required.`;
    else if (!blank && field.max && String(value).length > field.max)
      error = `Use ${field.max} characters or fewer.`;
    else if (!blank && field.min && String(value).length < field.min)
      error = `Use at least ${field.min} characters.`;
    else if (!blank && field.pattern && !field.pattern.test(String(value)))
      error = field.patternMessage;
    else if (!blank && ['email', 'emails'].includes(field.type)) {
      const emails =
        field.type === 'emails'
          ? String(value)
              .split(',')
              .map((email) => email.trim())
          : [String(value)];
      if (emails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)))
        error = 'Enter a valid email address.';
    } else if (!blank && field.type === 'url') {
      try {
        if (!['https:', 'http:'].includes(new URL(value).protocol))
          error = 'Enter an http or https website address.';
      } catch {
        error = 'Enter a valid website address.';
      }
    } else if (!blank && field.type === 'tel' && !/^\+?[0-9 ()-]+$/.test(value))
      error = 'Enter a valid phone number.';
    if (!error && field.validate) error = field.validate(value);
    if (error) errors[field.key] = error;
  }
  return errors;
};

export const fieldValues = (fields, source = {}) =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.type === 'password' && field.preserveBlank
        ? ''
        : field.type === 'emails' && Array.isArray(source[field.key])
          ? source[field.key].join(', ')
          : (source[field.key] ?? (field.type === 'boolean' ? false : '')),
    ]),
  );

export const changedFields = (fields, draft, saved, all = false) =>
  Object.fromEntries(
    fields
      .filter((field) => !field.preserveBlank || Boolean(String(draft[field.key] || '').trim()))
      .filter(
        (field) => all || JSON.stringify(draft[field.key]) !== JSON.stringify(saved[field.key]),
      )
      .map((field) => [
        field.key,
        field.type === 'emails'
          ? String(draft[field.key] || '')
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean)
          : draft[field.key] === ''
            ? null
            : draft[field.key],
      ]),
  );

// Navigation context is intentionally minimal. Service responses must be complete
// before controls are mounted; omitted data must not become editable defaults.
export function readAccountResponse(raw, targetShopId) {
  const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const hasFields = (value, keys) =>
    object(value) && keys.every((key) => Object.hasOwn(value, key));
  const target = raw?.target;
  let valid =
    object(raw) &&
    object(target) &&
    Number(target.id) === Number(targetShopId) &&
    typeof target.name === 'string' &&
    target.name.trim().length > 0 &&
    ['bayhive', 'autoflow', 'unclassified'].includes(target.ownership) &&
    typeof target.enabled === 'boolean';
  if (valid && target.ownership === 'bayhive') {
    valid =
      hasFields(
        raw.details,
        DETAILS_FIELDS.map((field) => field.key),
      ) &&
      hasFields(
        raw.address,
        ADDRESS_FIELDS.map((field) => field.key),
      ) &&
      hasFields(
        raw.texting,
        TEXTING_FIELDS.map((field) => field.key),
      ) &&
      hasFields(raw.mso, ['mso_id', 'mso_shop_id', 'isMaster', 'canManageGroup', 'shops']) &&
      Array.isArray(raw.mso.shops) &&
      Array.isArray(raw.users) &&
      Array.isArray(raw.numbers) &&
      object(raw.features) &&
      FEATURE_CONTROLS.every(
        (field) =>
          !Object.hasOwn(raw.features, field.key) ||
          isFeatureControlValue(field, raw.features[field.key]),
      ) &&
      (!Object.hasOwn(raw, 'kiosk') ||
        (hasFields(raw.kiosk, ['enabled', 'kioskUrl', 'schedulerUrl']) &&
          typeof raw.kiosk.enabled === 'boolean' &&
          [raw.kiosk.kioskUrl, raw.kiosk.schedulerUrl].every((url) => {
            if (url === null) return true;
            if (typeof url !== 'string') return false;
            try {
              const parsed = new URL(url);
              return (
                ['http:', 'https:'].includes(parsed.protocol) &&
                !parsed.username &&
                !parsed.password
              );
            } catch {
              return false;
            }
          }))) &&
      object(raw.options) &&
      ['roles', 'timezones', 'countries', 'organizations'].every((key) =>
        Array.isArray(raw.options[key]),
      );
  }
  if (!valid)
    throw new Error('Account details could not be confirmed. Try loading the account again.');
  return normalizeAccount(raw);
}

export function createHttpClient(endpoints = {}) {
  const endpoint = (key, targetShopId = null) => {
    if (!endpoints[key]) throw new Error('This account service is not available yet.');
    const url = new URL(
      endpoints[key].replace('{targetShopId}', encodeURIComponent(targetShopId)),
      window.location.origin,
    );
    if (url.origin !== window.location.origin) throw new Error('Invalid account service address.');
    return url.toString();
  };
  return {
    canRead: Boolean(endpoints.account),
    canMutate: Boolean(endpoints.mutate),
    canSearch: Boolean(endpoints.search),
    search: async (q) => {
      const data = (await axios.get(endpoint('search'), { params: { q, types: ['shop', 'mso'] } }))
        .data;
      const results = data.results || data.shops?.map((shop) => ({ ...shop, type: 'shop' }));
      if (
        !Array.isArray(results) ||
        results.some(
          (item) =>
            !['shop', 'mso'].includes(item.type) ||
            !Number.isSafeInteger(item.id) ||
            item.id < 1 ||
            typeof item.name !== 'string' ||
            (item.type === 'mso' &&
              (!Number.isInteger(item.locationCount) || item.locationCount < 0)),
        )
      )
        throw new Error('The search results could not be confirmed.');
      return results;
    },
    load: async (targetShopId) => (await axios.get(endpoint('account', targetShopId))).data,
    mutate: async (targetShopId, operation, changes) =>
      (await axios.post(endpoint('mutate', targetShopId), { targetShopId, operation, changes }))
        .data,
  };
}
